/** A state banner whose action is a real control.
 *
 *  `feedback/StateBanner.jsx` renders its action as a `<span onClick>` — the
 *  tenth component in the copied set with a handler and no semantics. It matters
 *  more here than in most: the banner exists to describe a problem, and its
 *  action is the route to fixing it. `NO TOKEN · ADD ONE` was unreachable by
 *  keyboard and unnamed to a screen reader, so the only way out of an
 *  unauthenticated session was to know Settings was there.
 *
 *  Same visual spec — an amber or red left edge, caps text, the action on the
 *  right. Reported upstream; delete this when StateBanner takes a button.
 */
export function Banner({
  kind = 'offline',
  text,
  action,
  onAction,
}: {
  /** `offline` and `stale` are amber conditions; `conflict` and `error` are red. */
  kind?: 'offline' | 'stale' | 'conflict' | 'error';
  text: string;
  action?: string;
  onAction?: () => void;
}) {
  const red = kind === 'conflict' || kind === 'error';
  const accent = red ? 'var(--bujo-state-error)' : 'var(--bujo-accent)';
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--bujo-space-3)',
        minHeight: 'var(--bujo-row-min)',
        padding: '0 var(--bujo-space-3)',
        background: 'var(--bujo-surface-sheet)',
        boxShadow: `inset var(--bujo-select-edge-width) 0 0 ${accent}`,
      }}
    >
      <span
        style={{
          flex: 1,
          minWidth: 0,
          font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1.5 var(--bujo-font-mono)',
          letterSpacing: 'var(--bujo-ls-badge)',
          color: accent,
        }}
      >
        {text}
      </span>
      {action && onAction ? (
        <button
          type="button"
          onClick={onAction}
          style={{
            flex: 'none',
            minHeight: 'var(--bujo-row-min)',
            padding: '0 var(--bujo-space-2)',
            background: 'none',
            border: 0,
            cursor: 'pointer',
            font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
            letterSpacing: 'var(--bujo-ls-label)',
            color: accent,
          }}
        >
          {action}
        </button>
      ) : null}
    </div>
  );
}
